import type { ReactNode } from 'react';

/**
 * A deliberately small Markdown renderer for report text the SERVER produced.
 *
 * It covers exactly what the post-event report emits — headings, pipe tables, bullet lists, bold and
 * paragraphs — and nothing else. Two reasons not to reach for a library here: the app ships no
 * Markdown dependency today, and a general renderer brings raw-HTML passthrough with it, which is a
 * hole waiting for the day this text starts carrying attendee comments verbatim (it already does).
 *
 * Nothing is ever interpreted as HTML: every leaf is React text, so a comment containing `<script>`
 * renders as those characters. Anything the grammar does not recognise falls through as a paragraph
 * rather than disappearing.
 */

/** Inline: **bold**, `code`. Everything else is literal text. */
function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[2] !== undefined) out.push(<strong key={`${keyBase}-b${i}`} className="font-semibold text-content">{m[2]}</strong>);
    else out.push(<code key={`${keyBase}-c${i}`} className="rounded bg-surface-2 px-1 py-px font-mono text-[0.85em]">{m[3]}</code>);
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const isTableRow = (l: string) => l.trim().startsWith('|') && l.trim().endsWith('|');
const cells = (l: string) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
/** The |---|:--:|---| separator under a table header. */
const isDivider = (l: string) => isTableRow(l) && cells(l).every((c) => /^:?-{2,}:?$/.test(c));

export function MiniMarkdown({ source, className = '' }: { source: string; className?: string }) {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }

    // Horizontal rule — the report uses it to divide narrative from figures.
    if (/^-{3,}$/.test(line.trim())) {
      blocks.push(<hr key={key++} className="my-5 border-line" />);
      i++;
      continue;
    }

    // Headings
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      const cls = level <= 2
        ? 'mt-5 mb-2 type-section-title text-content first:mt-0'
        : 'mt-4 mb-1.5 type-overline text-muted';
      blocks.push(<div key={key++} className={cls}>{inline(h[2], `h${key}`)}</div>);
      i++;
      continue;
    }

    // Table: a header row, its divider, then body rows.
    if (isTableRow(line) && i + 1 < lines.length && isDivider(lines[i + 1])) {
      const head = cells(line);
      i += 2;
      const body: string[][] = [];
      while (i < lines.length && isTableRow(lines[i])) { body.push(cells(lines[i])); i++; }
      // The report uses a headerless two-column table as a key/value panel; drop the empty header.
      const showHead = head.some((c) => c !== '');
      blocks.push(
        <div key={key++} className="my-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            {showHead && (
              <thead className="gb-thead-tint">
                <tr>
                  {head.map((c, n) => (
                    <th key={n} className="border-b border-line px-2 py-1.5 text-left type-overline text-subtle">{inline(c, `th${n}`)}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {body.map((row, rn) => (
                <tr key={rn} className="border-b border-line/60 last:border-0">
                  {row.map((c, cn) => (
                    <td key={cn} className={cn === row.length - 1 && row.length > 1 ? 'px-2 py-1.5 text-content' : 'px-2 py-1.5 text-muted'}>
                      {inline(c, `td${rn}-${cn}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Bullet list
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={key++} className="my-2 space-y-1.5">
          {items.map((it, n) => (
            <li key={n} className="flex gap-2 text-sm text-muted">
              <span className="mt-1.5 size-1 shrink-0 rounded-full bg-subtle/60" />
              <span className="min-w-0">{inline(it, `li${n}`)}</span>
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    // Paragraph — consecutive plain lines join, as Markdown does.
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,4})\s/.test(lines[i]) && !isTableRow(lines[i]) && !/^\s*[-*]\s+/.test(lines[i]) && !/^-{3,}$/.test(lines[i].trim())) {
      para.push(lines[i].trim());
      i++;
    }
    blocks.push(<p key={key++} className="my-2 text-sm leading-relaxed text-muted">{inline(para.join(' '), `p${key}`)}</p>);
  }

  return <div className={className}>{blocks}</div>;
}
